import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 320;
const MARGIN = { top: 30, right: 30, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function CounterfactualChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const n = data.dates.length;
    const parsedDates = data.dates.map((d) => new Date(d));
    const changeIdx = data.changePointIndex != null ? data.changePointIndex : n;
    const changeDate = data.changePoint ? new Date(data.changePoint) : null;

    // Build price domain from all visible series
    const allValues = [
      ...data.actual,
      ...data.counterfactual,
    ];
    if (data.confidence) {
      if (data.confidence.upper) allValues.push(...data.confidence.upper);
      if (data.confidence.lower) allValues.push(...data.confidence.lower);
    }
    const priceMin = d3.min(allValues.filter((v) => v != null));
    const priceMax = d3.max(allValues.filter((v) => v != null));
    const pricePadding = Math.max((priceMax - priceMin) * 0.1, 1);

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Confidence band around counterfactual (light gray shading)
    if (data.confidence && data.confidence.upper && data.confidence.lower) {
      const confidenceArea = d3.area()
        .x((d, i) => x(parsedDates[i]))
        .y0((d, i) => y(data.confidence.lower[i]))
        .y1((d, i) => y(data.confidence.upper[i]))
        .defined((d, i) => data.confidence.lower[i] != null && data.confidence.upper[i] != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.counterfactual)
        .attr('d', confidenceArea)
        .attr('fill', '#d1d5db')
        .attr('fill-opacity', 0.3)
        .attr('stroke', 'none');
    }

    // Shaded area between actual and counterfactual AFTER the change point
    // Green if actual > counterfactual, red if actual < counterfactual
    if (changeIdx < n) {
      // Build data points from change point onward
      const afterChangeData = [];
      for (let i = changeIdx; i < n; i++) {
        if (data.actual[i] != null && data.counterfactual[i] != null) {
          afterChangeData.push({
            index: i,
            actual: data.actual[i],
            counterfactual: data.counterfactual[i],
          });
        }
      }

      // Green area: actual above counterfactual
      const gapAreaGreen = d3.area()
        .x((d) => x(parsedDates[d.index]))
        .y0((d) => y(d.counterfactual))
        .y1((d) => y(d.actual))
        .defined((d) => d.actual >= d.counterfactual)
        .curve(d3.curveMonotoneX);

      // Red area: actual below counterfactual
      const gapAreaRed = d3.area()
        .x((d) => x(parsedDates[d.index]))
        .y0((d) => y(d.actual))
        .y1((d) => y(d.counterfactual))
        .defined((d) => d.actual < d.counterfactual)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(afterChangeData)
        .attr('d', gapAreaGreen)
        .attr('fill', '#16a34a')
        .attr('fill-opacity', 0.15)
        .attr('stroke', 'none');

      g.append('path')
        .datum(afterChangeData)
        .attr('d', gapAreaRed)
        .attr('fill', '#dc2626')
        .attr('fill-opacity', 0.15)
        .attr('stroke', 'none');
    }

    // Counterfactual line (dashed gray)
    const counterfactualLine = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.counterfactual)
      .attr('d', counterfactualLine)
      .attr('fill', 'none')
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,3');

    // Actual price line (solid dark blue)
    const actualLine = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.actual)
      .attr('d', actualLine)
      .attr('fill', 'none')
      .attr('stroke', '#1e3a5f')
      .attr('stroke-width', 2);

    // Vertical dashed line at change point
    if (changeDate) {
      const changeX = x(changeDate);

      g.append('line')
        .attr('x1', changeX)
        .attr('x2', changeX)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#6b7280')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3');

      // Change point label
      g.append('text')
        .attr('x', changeX)
        .attr('y', -8)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#6b7280')
        .text('Change Point');
    }

    // Gap annotation box (top-right of chart area)
    if (data.cumulativeGap != null) {
      const boxWidth = 160;
      const boxHeight = 46;
      const boxX = innerW - boxWidth - 8;
      const boxY = 8;

      const annotG = g.append('g').attr('transform', `translate(${boxX}, ${boxY})`);

      annotG.append('rect')
        .attr('width', boxWidth)
        .attr('height', boxHeight)
        .attr('rx', 6)
        .attr('fill', 'rgba(255, 255, 255, 0.9)')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      const gapPositive = data.cumulativeGap >= 0;
      const gapColor = gapPositive ? '#16a34a' : '#dc2626';
      const sign = gapPositive ? '+' : '';

      annotG.append('text')
        .attr('x', 8)
        .attr('y', 18)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', gapColor)
        .text(`Cumulative Gap: ${sign}$${Math.abs(data.cumulativeGap).toFixed(2)}`);

      if (data.cumulativeGapPercent != null) {
        annotG.append('text')
          .attr('x', 8)
          .attr('y', 34)
          .attr('font-size', '10px')
          .attr('fill', '#9ca3af')
          .text(`${sign}${data.cumulativeGapPercent.toFixed(1)}% vs expected trend`);
      }
    }

    // Crosshair and invisible overlay for mouse tracking
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const bisect = d3.bisector((d) => d).left;
        const dateAtMouse = x.invert(mx);
        const timestamps = parsedDates.map((d) => d.getTime());
        let idx = bisect(timestamps, dateAtMouse.getTime());
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = x(parsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        const actualVal = data.actual[idx];
        const counterfactualVal = data.counterfactual[idx];
        const gapVal = data.gap ? data.gap[idx] : null;

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          date: data.dates[idx],
          actual: actualVal,
          counterfactual: counterfactualVal,
          gap: gapVal,
          isAfterChange: idx >= changeIdx,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Counterfactual Analysis</div>
          <div style={styles.subtitle}>
            Actual prices vs projected trend — what would have been?
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No counterfactual data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Counterfactual Analysis</div>
        <div style={styles.subtitle}>
          Actual prices vs projected trend — what would have been?
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#1e3a5f" strokeWidth="2" />
          </svg>
          <span>Actual Price</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="2" strokeDasharray="4,2" />
          </svg>
          <span>Counterfactual (Expected)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#d1d5db" fillOpacity="0.3" rx="2" />
          </svg>
          <span>Confidence Band</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="10" height="10" fill="#16a34a" fillOpacity="0.15" rx="2" />
            <rect x="10" y="0" width="10" height="10" fill="#dc2626" fillOpacity="0.15" rx="2" />
          </svg>
          <span>Gap (green = above, red = below)</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Actual</span>
            <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
              ${tooltip.actual != null ? tooltip.actual.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Expected</span>
            <span style={{ fontWeight: 600, color: '#9ca3af' }}>
              ${tooltip.counterfactual != null ? tooltip.counterfactual.toFixed(2) : '--'}
            </span>
          </div>
          {tooltip.isAfterChange && tooltip.gap != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '4px', marginTop: '2px' }}>
              <span style={{ color: '#6b7280' }}>Gap</span>
              <span style={{ fontWeight: 600, color: tooltip.gap >= 0 ? '#16a34a' : '#dc2626' }}>
                {tooltip.gap >= 0 ? '+' : ''}${tooltip.gap.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
